import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  DataUsage as DataUsageIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

const DataDebugger = () => {
  const students = useSelector(state => state.students) || [];
  const teachers = useSelector(state => state.teachers) || [];
  const expenses = useSelector(state => state.expenses) || [];
  const sales = useSelector(state => state.sales?.sales) || [];
  const assets = useSelector(state => state.assets?.assets) || [];
  const schooldetails = useSelector(state => state.schooldetails) || [];

  const dataSummary = {
    students: {
      count: students.length,
      hasFinance: students.filter(s => s.finance && s.finance.length > 0).length,
      sample: students[0] || null
    },
    teachers: {
      count: teachers.length,
      sample: teachers[0] || null
    },
    expenses: {
      count: expenses.length,
      totalAmount: expenses.reduce((sum, exp) => sum + (parseFloat(exp.amountspent) || 0), 0),
      sample: expenses[0] || null
    },
    sales: {
      count: sales.length,
      totalRevenue: sales.reduce((sum, sale) => sum + (sale.total || 0), 0),
      sample: sales[0] || null
    },
    assets: {
      count: assets.length,
      totalValue: assets.reduce((sum, asset) => sum + (asset.purchaseCost || 0), 0),
      sample: assets[0] || null
    },
    schooldetails: {
      count: schooldetails.length,
      sample: schooldetails[0] || null
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <DataUsageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Data Debugger - Redux State Analysis
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Data Summary</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`Students: ${dataSummary.students.count}`} color="primary" />
          <Chip label={`Teachers: ${dataSummary.teachers.count}`} color="secondary" />
          <Chip label={`Expenses: ${dataSummary.expenses.count}`} color="error" />
          <Chip label={`Sales: ${dataSummary.sales.count}`} color="success" />
          <Chip label={`Assets: ${dataSummary.assets.count}`} color="info" />
          <Chip label={`Schools: ${dataSummary.schooldetails.count}`} color="warning" />
        </Box>
      </Box>

      {/* Students Data */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Students Data ({dataSummary.students.count} records)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Students with finance data: {dataSummary.students.hasFinance}
          </Typography>
          {dataSummary.students.sample && (
            <Box>
              <Typography variant="subtitle2">Sample Student Data:</Typography>
              <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(dataSummary.students.sample, null, 2)}
              </pre>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Teachers Data */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Teachers Data ({dataSummary.teachers.count} records)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {dataSummary.teachers.sample && (
            <Box>
              <Typography variant="subtitle2">Sample Teacher Data:</Typography>
              <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(dataSummary.teachers.sample, null, 2)}
              </pre>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Expenses Data */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Expenses Data ({dataSummary.expenses.count} records)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Total Amount: GHC {dataSummary.expenses.totalAmount.toLocaleString()}
          </Typography>
          {dataSummary.expenses.sample && (
            <Box>
              <Typography variant="subtitle2">Sample Expense Data:</Typography>
              <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(dataSummary.expenses.sample, null, 2)}
              </pre>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Sales Data */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Sales Data ({dataSummary.sales.count} records)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Total Revenue: GHC {dataSummary.sales.totalRevenue.toLocaleString()}
          </Typography>
          {dataSummary.sales.sample && (
            <Box>
              <Typography variant="subtitle2">Sample Sale Data:</Typography>
              <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(dataSummary.sales.sample, null, 2)}
              </pre>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Assets Data */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Assets Data ({dataSummary.assets.count} records)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Total Value: GHC {dataSummary.assets.totalValue.toLocaleString()}
          </Typography>
          {dataSummary.assets.sample && (
            <Box>
              <Typography variant="subtitle2">Sample Asset Data:</Typography>
              <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(dataSummary.assets.sample, null, 2)}
              </pre>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* School Details */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">School Details ({dataSummary.schooldetails.count} records)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {dataSummary.schooldetails.sample && (
            <Box>
              <Typography variant="subtitle2">Sample School Data:</Typography>
              <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(dataSummary.schooldetails.sample, null, 2)}
              </pre>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Financial Calculations Preview */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Financial Calculations Preview</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Amount (GHC)</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>School Fees Revenue</TableCell>
                  <TableCell align="right">
                    {students.reduce((total, student) => {
                      if (student.finance && student.finance.length > 0) {
                        const latestFinance = student.finance[student.finance.length - 1];
                        const totalFees = parseFloat(latestFinance.totalfees) || 0;
                        const arrears = parseFloat(latestFinance.Arrears) || 0;
                        return total + totalFees + arrears;
                      }
                      return total;
                    }, 0).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={students.length > 0 ? "Available" : "No Data"} 
                      color={students.length > 0 ? "success" : "error"} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Paid School Fees</TableCell>
                  <TableCell align="right">
                    {students.reduce((total, student) => {
                      if (student.finance && student.finance.length > 0) {
                        const latestFinance = student.finance[student.finance.length - 1];
                        if (latestFinance.payment && latestFinance.payment.length > 0) {
                          const totalPaid = latestFinance.payment.reduce((sum, payment) => {
                            if (payment.des !== "Discount") {
                              return sum + (parseFloat(payment.amountpaid) || 0);
                            }
                            return sum;
                          }, 0);
                          return total + totalPaid;
                        }
                      }
                      return total;
                    }, 0).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={students.some(s => s.finance && s.finance.length > 0) ? "Available" : "No Data"} 
                      color={students.some(s => s.finance && s.finance.length > 0) ? "success" : "error"} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Outstanding Fees</TableCell>
                  <TableCell align="right">
                    {students.reduce((total, student) => {
                      if (student.finance && student.finance.length > 0) {
                        const latestFinance = student.finance[student.finance.length - 1];
                        return total + (parseFloat(latestFinance.balance) || 0);
                      }
                      return total;
                    }, 0).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={students.some(s => s.finance && s.finance.length > 0) ? "Available" : "No Data"} 
                      color={students.some(s => s.finance && s.finance.length > 0) ? "success" : "error"} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Sales Revenue</TableCell>
                  <TableCell align="right">{dataSummary.sales.totalRevenue.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={sales.length > 0 ? "Available" : "No Data"} 
                      color={sales.length > 0 ? "success" : "error"} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total Expenses</TableCell>
                  <TableCell align="right">{dataSummary.expenses.totalAmount.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={expenses.length > 0 ? "Available" : "No Data"} 
                      color={expenses.length > 0 ? "success" : "error"} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Assets Value</TableCell>
                  <TableCell align="right">{dataSummary.assets.totalValue.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={assets.length > 0 ? "Available" : "No Data"} 
                      color={assets.length > 0 ? "success" : "error"} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DataDebugger;
